// components/common/Loading/Loading.tsx
import { ReactElement } from 'react';
import { Box, CircularProgress, Skeleton as MuiSkeleton, Typography, BoxProps } from '@mui/material';
import { styled, SxProps, Theme } from '@mui/material/styles';

export interface SpinnerProps extends BoxProps {
  size?: number;
  color?: string;
  thickness?: number;
  label?: string;
  fullPage?: boolean;
}

/**
 * Spinner - Circular loading indicator
 */
export function Spinner({
  size = 40,
  color = '#00FFD4',
  thickness = 4,
  label,
  fullPage = false,
  ...props
}: SpinnerProps): ReactElement {
  const spinner = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        ...props.sx,
      }}
    >
      <CircularProgress
        size={size}
        thickness={thickness}
        sx={{
          color: color,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          },
        }}
      />
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: '#8892A0',
            fontFamily: '"Exo 2", sans-serif',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );

  if (fullPage) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(10, 14, 20, 0.8)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
      >
        {spinner}
      </Box>
    );
  }

  return spinner;
}

/**
 * Skeleton - Loading placeholder
 */
const StyledSkeleton = styled(MuiSkeleton)({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  '&::after': {
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
  },
});

type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular';
type SkeletonAnimation = 'pulse' | 'wave' | false;

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  animation?: SkeletonAnimation;
  sx?: SxProps<Theme>;
}

export function Skeleton({
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
  ...props
}: SkeletonProps): ReactElement {
  return (
    <StyledSkeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        borderRadius: variant === 'circular' ? '50%' : '8px',
        ...props.sx,
      }}
      {...props}
    />
  );
}

export interface CardSkeletonProps {
  showHeader?: boolean;
  showImage?: boolean;
  lines?: number;
  sx?: SxProps<Theme>;
}

/**
 * CardSkeleton - Skeleton for card content
 */
export function CardSkeleton({
  showHeader = true,
  showImage = false,
  lines = 3,
  ...props
}: CardSkeletonProps): ReactElement {
  return (
    <Box
      sx={{
        backgroundColor: '#1E2530',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        p: 2,
        ...props.sx,
      }}
    >
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton width="40%" height={16} />
          </Box>
        </Box>
      )}

      {showImage && (
        <Skeleton
          height={160}
          sx={{ mb: 2 }}
        />
      )}

      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 ? '70%' : '100%'}
          height={16}
          sx={{ mb: i < lines - 1 ? 1 : 0 }}
        />
      ))}
    </Box>
  );
}

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  sx?: SxProps<Theme>;
}

/**
 * TableSkeleton - Skeleton for table rows
 */
export function TableSkeleton({ rows = 5, columns = 4, ...props }: TableSkeletonProps): ReactElement {
  return (
    <Box sx={props.sx}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              width={colIndex === 0 ? 60 : `${100 / (columns - 1)}%`}
              height={20}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export interface PageLoadingProps {
  message?: string;
}

/**
 * PageLoading - Full page loading state
 */
export function PageLoading({ message = 'Loading...' }: PageLoadingProps): ReactElement {
  return (
    <Box
      sx={{
        backgroundColor: '#0A0E14',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          animation: 'float 3s ease-in-out infinite',
          mb: 4,
        }}
      >
        <img
          src="/favicon.webp"
          alt="Loading"
          style={{
            width: '120px',
            filter: 'drop-shadow(0 0 30px rgba(0, 255, 212, 0.3))',
          }}
        />
      </Box>
      <Spinner size={48} label={message} />
    </Box>
  );
}

export default Spinner;
